import { CopyOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons'
import { Button, message as antMessage } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import useTypewriter from 'src/hooks/useTypewriter'
import { ollieMarkdownToPlainText } from 'src/utils/ollieMarkdown'
import {
  OLLIE_REPLY_FEEDBACK_REASONS,
  shouldAskOllieReplyFeedback,
} from 'src/utils/ollieReplyFeedback'
import { OllieMessageContent, OllieRichContent } from './ollieMessageContent'

const OllieMessageBubble = ({
  message,
  messages = null,
  onFeedback,
  isSubmittingFeedback = false,
  animate = false,
  onAnimateComplete,
  onAnimateTick,
  actions = [],
  onAction,
  actionsDisabled = false,
  feedbackFollowUp = null,
}) => {
  const isUser = message.role === 'user'
  const shouldAnimate = animate && !isUser
  const [revealComplete, setRevealComplete] = useState(!shouldAnimate)
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [comment, setComment] = useState('')
  const [reason, setReason] = useState(null)
  const askForFeedback = shouldAskOllieReplyFeedback({
    messages: messages || [message],
    messageId: message.id,
  })

  useEffect(() => {
    setRevealComplete(!shouldAnimate)
  }, [message.id, shouldAnimate])

  const handleRevealComplete = useCallback(() => {
    setRevealComplete(true)
    onAnimateComplete?.()
  }, [onAnimateComplete])

  const displayedText = useTypewriter(
    message.content,
    shouldAnimate && !revealComplete,
    {
      onComplete: handleRevealComplete,
      onTick: onAnimateTick,
    },
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        isUser ? message.content : ollieMarkdownToPlainText(message.content),
      )
      antMessage.success('Copied to clipboard')
    } catch (error) {
      antMessage.error('Could not copy to clipboard')
    }
  }

  const handleHelpful = () => {
    onFeedback(message.id, 'helpful')
  }

  const handleNotHelpfulClick = () => {
    setShowCommentForm(true)
  }

  const handleSubmitNotHelpful = () => {
    const trimmed = comment.trim()
    if (!reason && !trimmed) {
      antMessage.warning('Pick a reason or tell us what was missing')
      return
    }
    if (reason === 'other' && !trimmed) {
      antMessage.warning('Please tell us a bit more')
      return
    }
    onFeedback(message.id, 'not_helpful', trimmed, reason)
    setShowCommentForm(false)
    setComment('')
    setReason(null)
  }

  const feedbackRating = message.feedback?.rating
  const showActions = !isUser && revealComplete
  const showFeedbackButtons =
    askForFeedback && !feedbackRating && !showCommentForm

  return (
    <Row $isUser={isUser} data-ollie-message-id={message.id}>
      <Bubble $isUser={isUser}>
        <Content>
          {isUser ? (
            <OllieMessageContent content={message.content} />
          ) : revealComplete ? (
            <OllieRichContent content={message.content} />
          ) : (
            <OllieRichContent
              content={displayedText}
              partial
              trailing={<TypingCursor aria-hidden='true' />}
            />
          )}
        </Content>

        {!isUser && revealComplete && actions.length > 0 && onAction && (
          <ReplyButtons>
            {actions.map(action => (
              <ReplyButton
                key={`${action.index}-${action.id}`}
                type='button'
                disabled={actionsDisabled}
                onClick={() => onAction(message, action)}
              >
                {action.label}
              </ReplyButton>
            ))}
          </ReplyButtons>
        )}

        {!isUser && message.escalationNotice && revealComplete && (
          <EscalationNotice>{message.escalationNotice}</EscalationNotice>
        )}

        {showActions && (
          <Actions>
            <ActionButton
              type='button'
              onClick={handleCopy}
              title='Copy answer'
            >
              <CopyOutlined />
              <span>Copy</span>
            </ActionButton>

            {showFeedbackButtons && (
              <>
                <ActionButton
                  type='button'
                  onClick={handleHelpful}
                  disabled={isSubmittingFeedback}
                  title='Helpful'
                >
                  <LikeOutlined />
                  <span>Helpful</span>
                </ActionButton>
                <ActionButton
                  type='button'
                  onClick={handleNotHelpfulClick}
                  disabled={isSubmittingFeedback}
                  title='Not helpful'
                >
                  <DislikeOutlined />
                  <span>Not helpful</span>
                </ActionButton>
              </>
            )}

            {feedbackRating === 'helpful' && (
              <FeedbackThanks>Thanks for your feedback</FeedbackThanks>
            )}
            {feedbackRating === 'not_helpful' && (
              <FeedbackThanks>Feedback received — thank you</FeedbackThanks>
            )}
          </Actions>
        )}

        {showActions &&
          feedbackRating === 'not_helpful' &&
          feedbackFollowUp?.line && (
            <FollowUpLine>
              {feedbackFollowUp.line}
              {feedbackFollowUp.mailto && (
                <>
                  {' '}
                  <a href={feedbackFollowUp.mailto}>Email Stephen</a>
                </>
              )}
            </FollowUpLine>
          )}

        {showActions && showCommentForm && !feedbackRating && askForFeedback && (
          <CommentForm>
            <CommentLabel>What went wrong?</CommentLabel>
            <ReasonList role='radiogroup' aria-label='Not helpful reason'>
              {OLLIE_REPLY_FEEDBACK_REASONS.map(option => (
                <ReasonLabel key={option.id}>
                  <input
                    type='radio'
                    name={`ollie-feedback-${message.id}`}
                    checked={reason === option.id}
                    disabled={isSubmittingFeedback}
                    onChange={() => setReason(option.id)}
                  />
                  <span>{option.label}</span>
                </ReasonLabel>
              ))}
            </ReasonList>
            <CommentTextarea
              value={comment}
              onChange={event => setComment(event.target.value)}
              rows={2}
              placeholder='Optional detail…'
            />
            <CommentActions>
              <Button
                size='small'
                onClick={() => {
                  setShowCommentForm(false)
                  setComment('')
                  setReason(null)
                }}
              >
                Cancel
              </Button>
              <Button
                size='small'
                type='primary'
                loading={isSubmittingFeedback}
                onClick={handleSubmitNotHelpful}
              >
                Submit feedback
              </Button>
            </CommentActions>
          </CommentForm>
        )}
      </Bubble>
    </Row>
  )
}

const Row = styled.div`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  margin-bottom: 12px;
`

const Bubble = styled.div`
  max-width: 92%;
  padding: 14px 16px;
  border-radius: ${({ $isUser }) =>
    $isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px'};
  background: ${({ $isUser, theme }) =>
    $isUser ? theme.colours.lettuceGreen : theme.colours.mayoWhite};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border: 1px solid
    ${({ $isUser, theme }) =>
      $isUser ? theme.colours.lettuceGreen : theme.colours.panSilver};
  box-shadow: ${({ $isUser }) =>
    $isUser ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.04)'};
`

const Content = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.6;
`

const TypingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 1px;
  vertical-align: text-bottom;
  background: ${({ theme }) => theme.colours.oliveGrey};
  animation: ollieCursorBlink 1s step-end infinite;

  @keyframes ollieCursorBlink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`

const ReplyButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`

const ReplyButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const EscalationNotice = styled.p`
  margin: 10px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colours.bgGrey};
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  line-height: 1.4;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver};
`

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  cursor: pointer;
  padding: 2px 4px;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colours.bbqBlack};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const FeedbackThanks = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  align-self: center;
`

const FollowUpLine = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};

  a {
    color: ${({ theme }) => theme.colours.bbqBlack};
    font-weight: 600;
    text-decoration: underline;
  }
`

const CommentForm = styled.div`
  margin-top: 10px;
`

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`

const ReasonLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.bbqBlack};
  cursor: pointer;
`

const CommentLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const CommentTextarea = styled.textarea`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }
`

const CommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`

export default OllieMessageBubble
